/**
 * `wpp::context-menu`
 *
 * A menu raised by right-clicking an element, positioned at the pointer rather
 * than at the element — which is what makes it feel like a context menu instead
 * of a dropdown that happens to open on a different event. Flux does the same
 * thing by giving its popover a virtual reference at the cursor; here the panel
 * is fixed and placed directly, which needs no positioning library.
 *
 * Touch is handled too. There is no right-click on a touch screen, so a long
 * press opens the menu — without it the whole feature is unreachable on a
 * tablet, which is where a client list is quite likely to be read.
 *
 * @param {{ disabled?: boolean, longPressDelay?: number }} options
 */
export default (options = {}) => ({
    open: false,
    disabled: options.disabled ?? false,
    longPressDelay: options.longPressDelay ?? 500,

    /** Panel position, in viewport coordinates. */
    x: 0,
    y: 0,

    listeners: [],
    longPressTimer: null,
    longPressOrigin: null,

    init() {
        /*
         * Closing on scroll rather than following it. The menu was opened about
         * a specific place on the page; once that place has moved out from under
         * it, the menu is describing something the user can no longer see.
         */
        this.listen(window, 'scroll', () => this.close(), true);
        this.listen(window, 'resize', () => this.close());

        /*
         * A second right-click elsewhere should move the menu rather than leave
         * two open, and `contextmenu` outside our trigger would otherwise show
         * the browser's own menu over ours.
         */
        this.listen(document, 'contextmenu', (event) => {
            if (this.open && ! this.$refs.panel?.contains(event.target) && ! this.$el.contains(event.target)) {
                this.close();
            }
        });

        this.listen(document, 'livewire:navigating', () => this.close());
    },

    destroy() {
        this.cancelLongPress();
        this.listeners.forEach(({ target, event, handler, capture }) => {
            target.removeEventListener(event, handler, capture);
        });
        this.listeners = [];
    },

    listen(target, event, handler, capture = false) {
        target.addEventListener(event, handler, capture);
        this.listeners.push({ target, event, handler, capture });
    },

    /** Bound to the trigger with `x-bind="trigger"`. */
    trigger: {
        ['x-on:contextmenu.prevent']($event) {
            this.show($event.clientX, $event.clientY);
        },

        /*
         * Long press, for touch. Cancelled by any movement, because a press that
         * turns into a scroll was a scroll — opening a menu underneath a finger
         * that is already dragging the page is how a context menu becomes a
         * thing people fight with.
         */
        ['x-on:touchstart']($event) {
            if (this.disabled || $event.touches.length !== 1) {
                return;
            }

            const touch = $event.touches[0];
            this.longPressOrigin = { x: touch.clientX, y: touch.clientY };

            this.longPressTimer = setTimeout(() => {
                this.show(touch.clientX, touch.clientY);
            }, this.longPressDelay);
        },

        ['x-on:touchmove']($event) {
            if (! this.longPressOrigin || ! $event.touches.length) {
                return;
            }

            const touch = $event.touches[0];
            const moved = Math.hypot(
                touch.clientX - this.longPressOrigin.x,
                touch.clientY - this.longPressOrigin.y
            );

            if (moved > 10) {
                this.cancelLongPress();
            }
        },

        ['x-on:touchend']() {
            this.cancelLongPress();
        },

        ['x-on:touchcancel']() {
            this.cancelLongPress();
        },
    },

    cancelLongPress() {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
        this.longPressOrigin = null;
    },

    /**
     * @param {number} [clientX]
     * @param {number} [clientY]
     */
    show(clientX, clientY) {
        if (this.disabled) {
            return;
        }

        /*
         * Opened from the keyboard there is no pointer. The menu key fires
         * `contextmenu` like a right-click but reports 0 — or in some browsers a
         * negative — for both coordinates, which would put the menu in the corner
         * of the screen rather than on the thing it describes.
         */
        if (! Number.isFinite(clientX) || ! Number.isFinite(clientY) || clientX <= 0 || clientY <= 0) {
            return this.showOverElement();
        }

        this.open = true;

        this.$nextTick(() => {
            this.position(clientX, clientY);
            this.focusPanel();
        });
    },

    /** Open the menu over the element itself, for keyboard use. */
    showOverElement() {
        const rect = this.$el.getBoundingClientRect();

        this.disabled = this.disabled;
        this.open = true;

        this.$nextTick(() => {
            this.position(rect.left + 8, rect.top + 8);
            this.focusPanel();
        });
    },

    /**
     * Place the panel at the pointer, flipping back inside the viewport when it
     * would overflow — a menu opened near the right edge belongs to the left of
     * the cursor, not clipped off the screen.
     */
    position(clientX, clientY) {
        const panel = this.$refs.panel;

        if (! panel) {
            return;
        }

        const margin = 8;
        const { offsetWidth: width, offsetHeight: height } = panel;

        let x = clientX;
        let y = clientY;

        if (x + width > window.innerWidth - margin) {
            x = Math.max(margin, clientX - width);
        }

        if (y + height > window.innerHeight - margin) {
            y = Math.max(margin, clientY - height);
        }

        this.x = x;
        this.y = y;
    },

    close(focusTrigger = false) {
        if (! this.open) {
            return;
        }

        this.open = false;
        this.cancelLongPress();

        if (focusTrigger) {
            this.$el.focus?.();
        }
    },

    /** Bound to the panel with `x-bind="panel"`. */
    panel: {
        [':style']() {
            return `left: ${this.x}px; top: ${this.y}px;`;
        },
    },

    items() {
        return [...(this.$refs.panel?.querySelectorAll('[role="menuitem"]:not([disabled])') ?? [])];
    },

    /**
     * Focus the panel itself, not the first item.
     *
     * Landing on the first item makes it look chosen — the menu opens with
     * something already highlighted, which reads as a default action about to
     * happen. Focusing the panel keeps the menu unselected while still putting
     * keyboard focus inside it, so Escape closes and the arrow keys work; the
     * first Down then moves to the first item, which is where a selection should
     * begin.
     *
     * The panel carries `tabindex="-1"` so it can take focus programmatically
     * without joining the tab order.
     */
    focusPanel() {
        this.$refs.panel?.focus();
    },

    /**
     * Roving focus through the menu. A context menu opened by keyboard — the
     * menu key, or Shift+F10 — is only usable if the arrows work, and those are
     * the users most likely to be opening it that way.
     */
    onKeydown(event) {
        const items = this.items();

        if (items.length === 0) {
            return;
        }

        const current = items.indexOf(document.activeElement);

        const move = (index) => {
            event.preventDefault();
            items[(index + items.length) % items.length].focus();
        };

        /*
         * `current` is -1 while focus is still on the panel, so the first Down
         * lands on item 0 and the first Up on the last item.
         */
        switch (event.key) {
            case 'ArrowDown': return move(current + 1);
            case 'ArrowUp': return move(current < 0 ? items.length - 1 : current - 1);
            case 'Home': return move(0);
            case 'End': return move(items.length - 1);
            case 'Tab': return this.close();
        }
    },
});
